import type { SegmentDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Popover } from '../../ui/Popover.js'

/**
 * Which segments a contact is connected to, and the way to change it.
 *
 * **Only the connected ones.** A contact the rules of a segment match is in
 * that segment's audience too, and is deliberately not shown here: it would
 * mean running every segment's rules against every row of every page, and a
 * chip nobody can remove reads as broken.
 *
 * **Two chips and a count**, rather than a cell that grows until the table
 * cannot be read. The rest are behind the same control that adds one, which is
 * where somebody is going to look for them anyway.
 *
 * The picker ticks what is connected rather than offering "add" and "remove"
 * separately: there is one fact per segment — in or out — and two controls for
 * one fact is two places to disagree about it.
 */

/** Past this the cell stops being a cell, so the rest becomes a number. */
const SHOWN = 2

export function SegmentCell({
  connected,
  segments,
  busy,
  onChange,
}: {
  readonly connected: readonly { readonly segmentId: string; readonly name: string }[]
  /** Every segment the organisation has, which is what can be connected to. */
  readonly segments: readonly SegmentDto[]
  readonly busy: boolean
  readonly onChange: (segmentId: string, name: string, connecting: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const inside = new Set(connected.map((one) => one.segmentId))

  return (
    <span className="relative flex flex-wrap items-center gap-1">
      {connected.slice(0, SHOWN).map((one) => (
        <span
          className="max-w-[120px] truncate rounded-pill bg-brand-soft px-2 py-0.5 text-[11px] text-brand-strong"
          key={one.segmentId}
          title={one.name}
        >
          {one.name}
        </span>
      ))}
      {connected.length > SHOWN ? (
        <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-[11px] text-t2">
          +{connected.length - SHOWN}
        </span>
      ) : null}

      <Popover
        label="Connect this contact to a segment"
        onClose={() => setOpen(false)}
        onToggle={() => setOpen((was) => !was)}
        open={open}
        panelClassName="w-56 p-1"
        trigger="+"
        triggerClassName="h-5 w-5 flex-none rounded-full border border-line text-[12px] text-t2 leading-none hover:border-brand hover:text-brand"
      >
        {segments.length === 0 ? (
          <span className="block px-2 py-2 text-[12px] text-t3">
            No segments yet. Make one from Add view.
          </span>
        ) : (
          segments.map((segment) => (
            <button
              className="flex w-full items-center gap-2 rounded-field px-2 py-1.5 text-left text-[12px] text-t1 hover:bg-line-2 disabled:opacity-60"
              disabled={busy}
              key={segment.segmentId}
              onClick={() =>
                onChange(segment.segmentId, segment.name, !inside.has(segment.segmentId))
              }
              type="button"
            >
              <span
                className={`flex h-3.5 w-3.5 flex-none items-center justify-center rounded-[3px] border text-[9px] ${
                  inside.has(segment.segmentId)
                    ? 'border-brand bg-brand text-brand-contrast'
                    : 'border-line'
                }`}
              >
                {inside.has(segment.segmentId) ? '✓' : ''}
              </span>
              <span className="min-w-0 flex-1 truncate">{segment.name}</span>
            </button>
          ))
        )}
      </Popover>
    </span>
  )
}
